import { useEffect } from 'react';
import { Check } from 'lucide-react';
import { useExerciseProgress } from '../../lib/useSetProgress';
import { showStorageWarningOnce, showToast } from '../../lib/toast';

interface SetTrackerProps {
  dayKey: string;
  exerciseId: string;
  totalSets: number;
}

export function SetTracker({ dayKey, exerciseId, totalSets }: SetTrackerProps): JSX.Element {
  const { done, toggle, isPersistent } = useExerciseProgress(dayKey, exerciseId, totalSets);
  const doneSet = new Set(done);

  useEffect(() => {
    if (!isPersistent) showStorageWarningOnce();
  }, [isPersistent]);

  const onToggle = (n: number): void => {
    const wasDone = doneSet.has(n);
    toggle(n);
    if (wasDone) return; // Aus-Toggles werden nicht angekündigt
    const nextCount = done.length + 1;
    if (nextCount >= totalSets) {
      showToast('Uebung abgeschlossen', { variant: 'success', durationMs: 1500 });
    } else {
      showToast(`Set ${n} abgehakt`);
    }
  };

  return (
    <div className="flex flex-col gap-3">
      <p className="text-sm font-semibold text-zinc-600">
        Sets:{' '}
        <span className="tabular-nums text-zinc-900">
          {done.length}/{totalSets}
        </span>
      </p>
      <div className="flex flex-wrap gap-2">
        {Array.from({ length: totalSets }, (_, i) => i + 1).map((n) => {
          const isDone = doneSet.has(n);
          return (
            <button
              key={n}
              type="button"
              onClick={() => onToggle(n)}
              aria-pressed={isDone}
              aria-label={`Set ${n}${isDone ? ' abgehakt' : ''}`}
              className={`inline-flex size-14 items-center justify-center rounded-xl border-2 text-lg font-semibold tabular-nums transition-transform duration-[120ms] active:scale-[0.97] focus-visible:outline-2 focus-visible:outline-emerald-500 focus-visible:outline-offset-2 ${
                isDone
                  ? 'border-emerald-500 bg-emerald-500 text-white'
                  : 'border-zinc-300 bg-white text-zinc-900'
              }`}
            >
              {isDone ? <Check aria-hidden="true" className="size-6" /> : n}
            </button>
          );
        })}
      </div>
      {!isPersistent ? (
        <p className="text-xs text-amber-700">
          Fortschritt konnte nicht gespeichert werden. Pruefe Browser-Einstellungen.
        </p>
      ) : null}
    </div>
  );
}
